import { useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { LeadType } from '@econtractor/types';
import * as apiLeads from 'api/apiLeads';
import { useToast } from 'context/ToastContext';
import vconst from 'utils/helpers/structs/vconst';
import LeadStatus from '../LeadRows/LeadStatus';
import SolarIcon from 'components/icons/SolarIcon';

export interface LeadSelectedProps {
    value?: LeadType | null;
    onChange?: (lead: LeadType | null) => void;
    title?: string;
    rightElement?: ReactNode;
    maxHeight?: number;
    className?: string;
    disabled?: boolean;
    onPage?: number;
    clearable?: boolean;
    /** Лише перегляд вибраної заявки (без пошуку та очищення) */
    viewOnly?: boolean;
    /** Статуси для пошуку; якщо не передано — усі статуси */
    statuses?: number[];
    /** Лише монтування (без UI) — прогрів модуля / i18n */
    preload?: boolean;
}

/** Усі статуси заявок для пошуку без фільтра */
const ALL_LEAD_STATUSES = [
    vconst.leadNew,
    vconst.leadInWoork,
    vconst.leadNeedOffer,
    vconst.leadNeedsAttention,
    vconst.leadOffer,
    vconst.leadPostponed,
    vconst.leadAccept,
    vconst.leadReject,
];

const LeadNameItem = ({ lead }: { lead: LeadType }) => (
    <div className="min-w-0 overflow-hidden flex-grow-1">
        <h6 className="mb-1 text-truncate" title={lead.clientName}>
            {lead.clientName || '—'}
        </h6>
        <p className="text-muted f-12 mb-0 text-truncate" title={lead.clientPhone}>
            {lead.clientPhone || lead.clientEmail || ''}
        </p>
    </div>
);

const LeadSelected = ({
    value = null,
    onChange,
    title,
    rightElement = null,
    maxHeight = 220,
    className = '',
    disabled = false,
    onPage = 1000,
    clearable = true,
    viewOnly = false,
    statuses,
    preload = false,
}: LeadSelectedProps) => {
    const { t } = useTranslation(['Leads', 'common']);
    const { showToast } = useToast();
    const [search, setSearch] = useState('');
    const [leads, setLeads] = useState<LeadType[]>([]);
    const [searched, setSearched] = useState(false);
    const [loading, setLoading] = useState(false);
    const requestRef = useRef(0);

    if (preload) return null;

    const isInteractive = !viewOnly && !disabled;
    const canClear = clearable && isInteractive && Boolean(onChange);
    const isFixed = Boolean(value) || viewOnly;
    const showResults = !viewOnly && (loading || searched);
    const statusFilter =
        statuses && statuses.length > 0 ? statuses : ALL_LEAD_STATUSES;

    const fetchLeads = async () => {
        if (!isInteractive || loading || Boolean(value)) return;

        const requestId = requestRef.current + 1;
        requestRef.current = requestId;
        setLoading(true);
        const res = await apiLeads.getList({
            search: (search || '').trim(),
            page: 1,
            onPage,
            status: statusFilter,
        });
        if (requestRef.current !== requestId) return;
        setLoading(false);
        setSearched(true);

        if (!res) {
            setLeads([]);
            showToast(t('common:messError'), 'danger');
            return;
        }

        setLeads(res.leads || []);
    };

    const collapseSearch = () => {
        requestRef.current += 1;
        setLoading(false);
        setSearched(false);
        setLeads([]);
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            fetchLeads();
        }
    };

    const handleSelect = (lead: LeadType) => {
        if (!isInteractive || !onChange) return;
        onChange(lead);
        setSearch('');
        setLeads([]);
        setSearched(false);
    };

    const handleClear = () => {
        if (!canClear || !onChange) return;
        onChange(null);
        setSearch('');
        setLeads([]);
        setSearched(false);
    };

    const labelText = title || t('selectTitle');

    return (
        <div className={`user-selected ${className || 'mb-3'}`.trim()}>
            {(labelText || rightElement) && (
                <div className="d-flex justify-content-between align-items-center">
                    <label className={`form-label text-muted mb-1 ${labelText ? '' : 'd-none'}`}>
                        {labelText}
                    </label>
                    {rightElement}
                </div>
            )}

            {isFixed ? (
                <div className="user-selected-fixed">
                    <div className="user-selected-fixed-user d-flex align-items-center gap-2 min-w-0">
                        {value ? (
                            <>
                                <LeadNameItem lead={value} />
                                <div className="flex-shrink-0">
                                    <LeadStatus leadStatus={value.leadStatus || 0} className="fs-10" />
                                </div>
                            </>
                        ) : (
                            <span className="text-muted">—</span>
                        )}
                    </div>
                    {canClear && (
                        <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="user-selected-clear"
                            title={t('common:buttons.clear')}
                            onClick={handleClear}
                        >
                            <SolarIcon name="x-lg" />
                        </Button>
                    )}
                </div>
            ) : (
                <div className={`user-selected-panel${showResults ? ' is-open' : ''}`}>
                    <InputGroup className="user-selected-search">
                        <Form.Control
                            type="text"
                            value={search}
                            disabled={disabled || loading}
                            placeholder={t('selectSearchPlaceholder')}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={handleKeyDown}
                        />
                        {showResults ? (
                            <Button
                                type="button"
                                variant="link"
                                className="user-selected-search-close"
                                title={t('common:buttons.close')}
                                onClick={collapseSearch}
                            >
                                <SolarIcon name="x-lg" />
                            </Button>
                        ) : null}
                        <Button
                            type="button"
                            variant="link"
                            className="user-selected-search-btn"
                            disabled={disabled || loading}
                            title={t('selectSearchBtn')}
                            onClick={fetchLeads}
                        >
                            {loading ? (
                                <Spinner animation="border" size="sm" variant="primary" />
                            ) : (
                                <SolarIcon name="search" />
                            )}
                        </Button>
                    </InputGroup>

                    {showResults && (
                        <div className="user-selected-results position-relative" style={{ maxHeight }}>
                            {loading && (
                                <div className="user-selected-empty">
                                    <Spinner animation="border" size="sm" variant="primary" className="opacity-75" />
                                </div>
                            )}

                            {!loading && leads.length === 0 && (
                                <div className="user-selected-empty">
                                    {t('selectNoResults')}
                                </div>
                            )}

                            {!loading && leads.length > 0 && (
                                <ul className="user-selected-list">
                                    {leads.map((lead) => (
                                        <li key={lead.ID}>
                                            <button
                                                type="button"
                                                className="user-selected-item"
                                                disabled={disabled}
                                                onClick={() => handleSelect(lead)}
                                            >
                                                <LeadNameItem lead={lead} />
                                                <div className="flex-shrink-0">
                                                    <LeadStatus leadStatus={lead.leadStatus || 0} className="fs-10" />
                                                </div>
                                                <SolarIcon name="chevron-right" className="user-selected-item-chevron" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default LeadSelected;
